/**
 * F2 (14/08) — o INVENTÁRIO COMPLETO dos campos configuráveis do cadastro,
 * organizado por régua de etiqueta. Fonte única: a config (AdminConfigPage),
 * o aviso do "quantos campos o rápido mostrará" e, nas fatias F3/F4, os
 * formulários dinâmicos leem DAQUI — inventário divergente entre telas é o
 * bug que esta lista existe pra impedir.
 *
 * A medição que originou a lista (13/08): a tela antiga cobria 5 campos de
 * ~48 — "não cobre todos os campos e abas do cadastro" (Matheus).
 *
 * Regras do desenho aprovado:
 *  · etiqueta '' = régua GLOBAL (exige de todo cadastro);
 *  · seções por etiqueta usam as grafias EXATAS do Omie;
 *  · a régua SOMA: quem tem duas etiquetas preenche os dois conjuntos;
 *  · "pelo menos um" vale pras LISTAS (e-mails, contatos, etiquetas) — o
 *    motor já trata array vazio como vazio.
 */

export interface CampoInventario {
  fieldName: string;
  label: string;
  hint?: string;
  /** Listas (e-mails/contatos/etiquetas): exigência é "pelo menos um". */
  lista?: boolean;
}

export interface SecaoInventario {
  /** '' = régua global. Senão, grafia EXATA da etiqueta do Omie. */
  etiqueta: '' | 'Cliente' | 'Funcionário' | 'Fornecedor' | 'Transportadora';
  titulo: string;
  descricao: string;
  campos: CampoInventario[];
}

/** Campos comuns a qualquer cadastro (blocos Identificação → Entrega da ficha). */
const CAMPOS_COMUNS: CampoInventario[] = [
  // Identificação
  { fieldName: 'razaoSocial', label: 'Razão social / Nome' },
  { fieldName: 'nomeFantasia', label: 'Nome fantasia' },
  { fieldName: 'documento', label: 'CNPJ ou CPF', hint: 'Sem marcar aqui, ele continua exigido só no Ganho' },
  { fieldName: 'telefone', label: 'Telefone' },
  { fieldName: 'numUnidades', label: 'Nº de unidades' },
  // Fiscal
  { fieldName: 'ie', label: 'Inscrição Estadual' },
  { fieldName: 'im', label: 'Inscrição Municipal' },
  { fieldName: 'optanteSimples', label: 'Simples Nacional' },
  { fieldName: 'contribuinteIcms', label: 'Contribuinte ICMS' },
  { fieldName: 'produtorRural', label: 'Produtor rural' },
  { fieldName: 'suframa', label: 'SUFRAMA' },
  { fieldName: 'tipoAtividade', label: 'Tipo de atividade' },
  { fieldName: 'cnae', label: 'CNAE principal' },
  // Bancários
  { fieldName: 'banco', label: 'Banco' },
  { fieldName: 'agencia', label: 'Agência' },
  { fieldName: 'conta', label: 'Conta' },
  { fieldName: 'pix', label: 'Chave Pix' },
  { fieldName: 'titularNome', label: 'Titular da conta' },
  { fieldName: 'titularDocumento', label: 'CNPJ/CPF do titular' },
  // Faturamento e recomendações
  { fieldName: 'telefone2', label: 'Telefone 2' },
  { fieldName: 'website', label: 'Site' },
  { fieldName: 'emailNfe', label: 'E-mail para NF-e' },
  { fieldName: 'transportadora', label: 'Transportadora (nome)' },
  { fieldName: 'parcelasPadrao', label: 'Parcelas padrão' },
  { fieldName: 'vendedorPadraoOmie', label: 'Vendedor padrão (Omie)' },
  { fieldName: 'gerarBoleto', label: 'Gerar boleto' },
  { fieldName: 'codigoReceita', label: 'Código de receita' },
  { fieldName: 'limiteCreditoCentavos', label: 'Limite de crédito' },
  { fieldName: 'bloquearFaturamento', label: 'Bloquear faturamento' },
  // Endereço fiscal
  { fieldName: 'enderecoLogradouro', label: 'Endereço — logradouro' },
  { fieldName: 'enderecoNumero', label: 'Endereço — número' },
  { fieldName: 'enderecoBairro', label: 'Endereço — bairro' },
  { fieldName: 'enderecoCep', label: 'Endereço — CEP' },
  { fieldName: 'enderecoCidade', label: 'Endereço — cidade' },
  { fieldName: 'enderecoUf', label: 'Endereço — UF' },
  // Entrega (quando ≠ fiscal)
  { fieldName: 'entregaLogradouro', label: 'Entrega — logradouro' },
  { fieldName: 'entregaNumero', label: 'Entrega — número' },
  { fieldName: 'entregaBairro', label: 'Entrega — bairro' },
  { fieldName: 'entregaCep', label: 'Entrega — CEP' },
  { fieldName: 'entregaCidade', label: 'Entrega — cidade' },
  { fieldName: 'entregaUf', label: 'Entrega — UF' },
  // Listas ("pelo menos um")
  { fieldName: 'emails', label: 'E-mails', hint: 'Pelo menos um e-mail cadastrado', lista: true },
  { fieldName: 'contatos', label: 'Contatos', hint: 'Pelo menos uma pessoa de contato', lista: true },
  { fieldName: 'etiquetas', label: 'Etiquetas (segmento)', hint: 'Pelo menos uma etiqueta — vale só no Cadastro completo; o rápido já nasce Cliente', lista: true },
];

const CAMPOS_FUNCIONARIO: CampoInventario[] = [
  { fieldName: 'cargoFuncionario', label: 'Cargo na IGM' },
  { fieldName: 'dataAdmissao', label: 'Data de admissão' },
  { fieldName: 'dataDemissao', label: 'Data de demissão' },
  { fieldName: 'dataAniversario', label: 'Data de aniversário' },
  { fieldName: 'cltRegistro', label: 'Registro CLT' },
  { fieldName: 'cboCodigo', label: 'Código CBO' },
];

const CAMPOS_FORNECEDOR: CampoInventario[] = [
  { fieldName: 'fornFazManutencao', label: 'Faz manutenção de produtos?' },
  { fieldName: 'fornMinFaturarVendaCentavos', label: 'Mínimo para faturar — venda' },
  { fieldName: 'fornMinFaturarManutencaoCentavos', label: 'Mínimo para faturar — manutenção' },
  { fieldName: 'fornImpostoPct', label: '% de imposto' },
  { fieldName: 'fornCobraImpostoRetirada', label: 'Cobra imposto na retirada do crédito?' },
  { fieldName: 'fornPedeNfRetirada', label: 'Pede NF na retirada do crédito?' },
  { fieldName: 'fornPedeNfAbatimento', label: 'Pede NF no abatimento?' },
];

/**
 * As cinco seções da tela de config. Cada seção de etiqueta lista os campos
 * ESPECÍFICOS dela mais os comuns — exigir "telefone só de Cliente" é marcar
 * telefone na seção Cliente em vez da Geral.
 */
export const SECOES_INVENTARIO: SecaoInventario[] = [
  {
    etiqueta: '',
    titulo: 'Geral — todo cadastro',
    descricao: 'Exigido de qualquer cadastro, com ou sem etiqueta.',
    campos: CAMPOS_COMUNS,
  },
  {
    etiqueta: 'Cliente',
    titulo: 'Cliente',
    descricao: 'Exigido de quem tem a etiqueta Cliente — inclui todo cadastro feito pelo funil.',
    campos: CAMPOS_COMUNS.filter((c) => c.fieldName !== 'etiquetas'),
  },
  {
    etiqueta: 'Funcionário',
    titulo: 'Funcionário',
    descricao: 'Exigido de quem tem a etiqueta Funcionário. O salário fica fora — é lançamento, não campo.',
    campos: [...CAMPOS_FUNCIONARIO, ...CAMPOS_COMUNS.filter((c) => c.fieldName !== 'etiquetas')],
  },
  {
    etiqueta: 'Fornecedor',
    titulo: 'Fornecedor',
    descricao: 'Exigido de quem tem a etiqueta Fornecedor (regras de faturamento da aba Fornecedor).',
    campos: [...CAMPOS_FORNECEDOR, ...CAMPOS_COMUNS.filter((c) => c.fieldName !== 'etiquetas')],
  },
  {
    etiqueta: 'Transportadora',
    titulo: 'Transportadora',
    descricao: 'Exigido de quem tem a etiqueta Transportadora.',
    campos: CAMPOS_COMUNS.filter((c) => c.fieldName !== 'etiquetas'),
  },
];

/**
 * Quantos campos o CADASTRO RÁPIDO do funil passará a mostrar com a régua
 * atual (união global ∪ Cliente). O aviso existe porque cada campo exigido
 * de Cliente entra no fluxo do vendedor — exigir demais faz o "rápido"
 * deixar de ser rápido, e sem o número o admin só descobre usando.
 */
export function contarCamposDoRapido(
  rows: Array<{ etiqueta?: string; required: boolean; fieldName: string; entityType: string }>,
): number {
  const nomes = new Set<string>();
  for (const r of rows) {
    if (r.entityType !== 'client' || !r.required) continue;
    const et = r.etiqueta ?? '';
    if (et === '' || et === 'Cliente') nomes.add(r.fieldName);
  }
  return nomes.size;
}
